import React from 'react';
import {
  FileText,
  Camera,
  MapPin,
  Bell,
  Scale,
  CheckCircle2,
  MessageSquare,
  Star,
  ShieldCheck,
  ArrowRight,
  HelpCircle,
  Clock,
  Sparkles,
  Lock,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';

export const CustomerGuidePage: React.FC = () => {
  const { navigateTo } = useApp();

  const customerSteps = [
    {
      title: '1. Post Your Job Details',
      desc: 'Describe what needs repair, servicing, or construction. State specific issues like a pipe burst, tripped breaker, or new furniture.',
      icon: FileText,
    },
    {
      title: '2. Upload Photos or Video',
      desc: 'Photos of the faulty area, model label, or workspace help professionals assess materials and complexity before quoting.',
      icon: Camera,
    },
    {
      title: '3. Select Nigerian Location',
      desc: 'Pick your State and Local Government Area (LGA) so matching happens with professionals near you, avoiding transit markups.',
      icon: MapPin,
    },
    {
      title: '4. Local Tradespeople Notified',
      desc: 'JobMatch NG identifies verified professionals specializing in your requested trade and alerts them to submit bids.',
      icon: Bell,
    },
    {
      title: '5. Compare Itemized Quotes',
      desc: 'Review transparent quotes with separate labor and materials cost estimates in Naira, along with professional badges and ratings.',
      icon: Scale,
    },
    {
      title: '6. Select Your Professional',
      desc: 'Accept the quote that matches your schedule and budget. Contact details are unlocked immediately for coordination.',
      icon: CheckCircle2,
    },
    {
      title: '7. Direct Communication',
      desc: 'Message or call directly to discuss arrival times, site access, material purchases, and any technical questions.',
      icon: MessageSquare,
    },
    {
      title: '8. Complete & Review',
      desc: 'Inspect the finished work with the professional, finalize agreed payment, and leave a review to guide future Nigerian clients.',
      icon: Star,
    },
  ];

  const safetyTips = [
    'Always confirm quote items and material costs in writing through the platform before work commences.',
    'For major electrical or plumbing alterations, switch off main breakers or control valves prior to work.',
    'Inspect completed repairs thoroughly with the professional before settling the final balance.',
    'Report any misconduct or unapproved markups immediately to the JobMatch NG Trust Desk.',
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8 space-y-12">
      {/* Hero Header */}
      <div className="max-w-5xl mx-auto bg-slate-900 text-white rounded-3xl p-6 sm:p-12 border border-slate-800 shadow-xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />
        <div className="relative max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span>Customer Service Guide</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
            How Customers Hire on JobMatch NG
          </h1>

          <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
            Finding reliable, vetted technical tradespeople in Nigeria shouldn't be stressful.
            Post your job for free, receive transparent quotes, and hire verified professionals with confidence.
          </p>

          <div className="pt-2">
            <button
              onClick={() => navigateTo('post_job')}
              className="px-6 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs sm:text-sm font-bold shadow-lg shadow-emerald-600/30 transition-all inline-flex items-center gap-2 cursor-pointer"
            >
              <span>Post a Job Free</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* 8-Step Walkthrough */}
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
            From Problem to Fix in 8 Simple Steps
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 max-w-xl mx-auto">
            Our streamlined process ensures you stay in control of pricing, scheduling, and professional selection.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {customerSteps.map((step) => {
            const Icon = step.icon;
            return (
              <div
                key={step.title}
                className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs flex items-start gap-4"
              >
                <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                  <Icon className="w-6 h-6" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="font-bold text-base text-slate-900">{step.title}</h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Safety & Hiring Best Practices */}
      <div className="max-w-4xl mx-auto bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-sm space-y-6">
        <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
          <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0" />
          <div>
            <h3 className="font-extrabold text-lg text-slate-900">
              Customer Tips for Safe & Quality Hiring
            </h3>
            <p className="text-xs text-slate-500">
              Smart practices when bringing technical craftspeople into your home or commercial premises.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {safetyTips.map((tip, idx) => (
            <div
              key={idx}
              className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 text-xs text-slate-700 leading-relaxed flex items-start gap-2.5"
            >
              <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 font-bold flex items-center justify-center shrink-0 text-[11px]">
                {idx + 1}
              </span>
              <span>{tip}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Final Action CTA */}
      <div className="max-w-4xl mx-auto bg-slate-900 text-white rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-xl">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
          Have a Repair or Maintenance Need Today?
        </h2>
        <p className="text-xs sm:text-sm text-slate-300 max-w-xl mx-auto leading-relaxed">
          Post your job in minutes. There are no upfront fees to post or compare quotes.
        </p>

        <button
          onClick={() => navigateTo('post_job')}
          className="px-8 py-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-lg shadow-emerald-600/30 transition-all inline-flex items-center gap-2 cursor-pointer"
        >
          <span>Post a Job Free</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
